"use client";

import React, { useState, useEffect } from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Send, HeartHandshake, Sparkles, CloudUpload } from "lucide-react";
import { cn } from "@/lib/utils";
export default function VolunteerApplication() {
  return <section data-section-id="143583" id="volunteer-inquiry" className="dark w-full py-16 md:py-24 bg-background text-foreground relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute bottom-[10%] -left-[10%] w-[40%] h-[40%] rounded-full bg-chart-3/10 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10 max-w-3xl">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center p-3 bg-primary/20 rounded-2xl mb-4 text-primary">
            <HeartHandshake className="size-8" />
          </div>
          <h2 className="text-4xl md:text-6xl font-bold tracking-tighter text-foreground mb-4">
            Share Your Expertise
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-xl mx-auto font-sans">
            Help us transform lives through education in Ghana. Your professional skills and mentorship can create lasting change for young scholars.
          </p>
        </div>

        <div className="bg-card/90 backdrop-blur-2xl border border-border/60 shadow-2xl rounded-3xl p-6 md:p-10">
          <FormProvider formKey="volunteer_inquiry_form_contact" formId="volunteer_inquiry_form_contact" sectionName="volunteer-application" defaultValues={{
          full_name: "",
          email: "",
          phone: "",
          city: "",
          availability: "",
          skills_experience: "",
          motivation: "",
          resume: undefined
        }} validationRules={{
          full_name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true,
            pattern: "phone"
          },
          availability: {
            required: true
          },
          skills_experience: {
            required: true
          },
          motivation: {
            required: true
          }
        }} submitText="Submit Inquiry" successMessage="Thank you for your interest! We will review your inquiry and contact you about how you can support our mission in Ghana.">
            <VolunteerFormContent />
          </FormProvider>
        </div>
      </div>
      <Toaster position="top-center" />
    </section>;
}
function VolunteerFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [step, setStep] = useState(1);
  const totalSteps = 3;
  useEffect(() => {
    if (isSubmitted && !submitError) {
      toast.success(successMessage, {
        icon: <Sparkles className="size-5 text-primary" />,
        duration: 5000
      });
    } else if (isSubmitted && submitError) {
      toast.error(submitError);
    }
  }, [isSubmitted, submitError, successMessage]);
  const nextStep = () => setStep(s => Math.min(s + 1, totalSteps));
  const prevStep = () => setStep(s => Math.max(s - 1, 1));
  if (isSubmitted && !submitError) {
    return <div className="text-center py-12 animate-in fade-in zoom-in duration-500">
        <div className="inline-flex items-center justify-center size-20 bg-chart-3/20 text-chart-3 rounded-full mb-6">
          <HeartHandshake className="size-10" />
        </div>
        <h3 className="text-3xl font-bold text-foreground mb-2">Inquiry Received!</h3>
        <p className="text-muted-foreground mb-8 max-w-md mx-auto font-sans">
          {successMessage}
        </p>
        <Button onClick={() => {
        resetForm();
        setStep(1);
      }} variant="outline" size="lg" className="rounded-full border-primary text-primary hover:bg-primary/10">
          Send Another Inquiry
        </Button>
      </div>;
  }
  return <div className="space-y-8">
      {}
      <div className="space-y-2">
        <div className="flex justify-between text-sm font-mono text-muted-foreground px-1 uppercase tracking-widest">
          <span>Step {step} of {totalSteps}</span>
          <span>{Math.round(step / totalSteps * 100)}%</span>
        </div>
        <Progress value={step / totalSteps * 100} className="h-2 bg-muted/30" />
      </div>

      <Form onSubmit={handleSubmit} className="space-y-6">
        {}
        <div className={cn("space-y-6 animate-in slide-in-from-right-4 fade-in duration-300", step !== 1 && "hidden")}>
          <div className="mb-6">
            <h3 className="text-2xl font-bold text-foreground tracking-tight">Your Profile</h3>
            <p className="text-sm text-muted-foreground font-sans">Please provide your contact information so we can reach out.</p>
          </div>

          <FormField control={control} name="full_name" rules={{
          required: "Full name is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-base font-semibold">Full Name *</FormLabel>
                <FormControl>
                  <Input className="h-12 text-base rounded-xl bg-background/40 border-border/50 focus:border-primary focus:ring-primary/20" placeholder="Enter your full name" {...field} />
                </FormControl>
                <FormDescription className="text-xs font-sans">As listed on your professional identification.</FormDescription>
                <FormMessage />
              </FormItem>} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField control={control} name="email" rules={{
            required: "Email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Invalid email address"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-base font-semibold">Email Address *</FormLabel>
                  <FormControl>
                    <Input type="email" className="h-12 text-base rounded-xl bg-background/40 border-border/50 focus:border-primary focus:ring-primary/20" placeholder="email@address.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="phone" rules={{
            required: "Phone number is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-base font-semibold">Phone Number *</FormLabel>
                  <FormControl>
                    <Input type="tel" className="h-12 text-base rounded-xl bg-background/40 border-border/50 focus:border-primary focus:ring-primary/20" placeholder="+44 000 000 0000" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          <FormField control={control} name="city" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-base font-semibold">City / Region</FormLabel>
                <FormControl>
                  <Input className="h-12 text-base rounded-xl bg-background/40 border-border/50 focus:border-primary focus:ring-primary/20" placeholder="e.g. London, UK" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        {}
        <div className={cn("space-y-6 animate-in slide-in-from-right-4 fade-in duration-300", step !== 2 && "hidden")}>
          <div className="mb-6">
            <h3 className="text-2xl font-bold text-foreground tracking-tight">Your Contribution</h3>
            <p className="text-sm text-muted-foreground font-sans">Tell us how you would like to support our educational programs.</p>
          </div>

          <FormField control={control} name="availability" rules={{
          required: "Please select your availability"
        }} render={({
          field
        }) => <FormItem className="space-y-3">
                <FormLabel className="text-base font-semibold">Availability *</FormLabel>
                <FormControl>
                  <ToggleGroup type="single" variant="outline" className="flex flex-wrap gap-2 justify-start w-full" value={field.value} onValueChange={field.onChange}>
                    {["Remote Mentorship", "Administrative Support", "Project Planning", "On-site (Ghana)"].map((option, i) => <ToggleGroupItem key={i} value={option} className="h-12 px-5 rounded-xl border-border/50 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary transition-all font-medium" aria-label={`Select ${option}`}>
                        {option}
                      </ToggleGroupItem>)}
                  </ToggleGroup>
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="skills_experience" rules={{
          required: "Please describe your skills"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-base font-semibold">Specialist Skills / Experience *</FormLabel>
                <FormControl>
                  <textarea className="flex min-h-[120px] w-full rounded-xl border border-border/50 bg-background/40 px-3 py-3 text-base shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 resize-y font-sans" placeholder="e.g., Education management, grant writing, IT training, financial planning..." {...field} />
                </FormControl>
                <FormDescription className="text-xs font-sans">Describe any professional training or expertise relevant to advancing education in disadvantaged areas.</FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>

        {}
        <div className={cn("space-y-6 animate-in slide-in-from-right-4 fade-in duration-300", step !== 3 && "hidden")}>
          <div className="mb-6">
            <h3 className="text-2xl font-bold text-foreground tracking-tight">Your Motivation</h3>
            <p className="text-sm text-muted-foreground font-sans">Why are you passionate about supporting education in Ghana?</p>
          </div>

          <FormField control={control} name="motivation" rules={{
          required: "Please share your motivation"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-base font-semibold">Why do you want to volunteer with Farquhar Fisher Foundation? *</FormLabel>
                <FormControl>
                  <textarea className="flex min-h-[150px] w-full rounded-xl border border-border/50 bg-background/40 px-3 py-3 text-base shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 resize-y font-sans" placeholder="Tell us about your connection to our mission..." {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="resume" render={({
          field: {
            value,
            onChange,
            ...fieldProps
          }
        }) => <FormItem>
                <FormLabel className="text-base font-semibold">Upload CV/Portfolio (optional)</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input type="file" accept=".pdf,.doc,.docx" className="h-14 pt-3.5 pl-12 text-base rounded-xl bg-background/40 border-border/50 file:mr-4 file:py-1 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary/20 file:text-primary hover:file:bg-primary/30 cursor-pointer" onChange={e => {
                const file = e.target.files?.[0];
                onChange(file);
              }} {...fieldProps} />
                    <CloudUpload className="absolute left-4 top-4 size-5 text-muted-foreground pointer-events-none" />
                  </div>
                </FormControl>
                <FormDescription className="text-xs font-sans">PDF or DOCX format preferred, max 2 MB.</FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>

        {}
        <div className="flex items-center justify-between pt-6 border-t border-border/50 mt-8">
          {step > 1 ? <Button type="button" variant="ghost" onClick={prevStep} className="rounded-full h-12 px-6 hover:bg-muted/30 text-muted-foreground">
              <ArrowLeft className="mr-2 size-4" /> Back
            </Button> : <div></div>}

          {step < totalSteps ? <Button type="button" onClick={nextStep} className="rounded-full h-12 px-8 text-base font-semibold bg-primary text-primary-foreground hover:opacity-90 transition-opacity">
              Continue <ArrowRight className="ml-2 size-4" />
            </Button> : <Button type="submit" disabled={isSubmitting} className="rounded-full h-12 px-8 text-base font-bold shadow-xl shadow-primary/10 bg-primary text-primary-foreground hover:scale-[1.02] transition-all">
              {isSubmitting ? <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-primary-foreground" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span> : <span className="flex items-center">
                  Submit Inquiry <Send className="ml-2 size-4" />
                </span>}
            </Button>}
        </div>
      </Form>
    </div>;
}